import { RawDraftContentState } from 'draft-js';
import { isNumber, isString } from 'lodash';
import { CellData, TableContentData } from '../../../../common/table/TableTypes';
import {
    isCellValueInDraftFormat,
    parseCellContentString,
} from '../../../../common/table/utils/tableCellContentStringUtils';
import rawGetText from '../../../../common/utils/editor/rawUtils/rawGetText';

export const EMPTY_TABLE_CONTENT_DATA: TableContentData = [[]];

/**
 * Adds the empty entity map object to draftContent.
 *
 * This is required, otherwise the Draft.js decodeRawEntityMap with throw an error.
 */
const normalizeDraftContentEntityMap = (draftContent: RawDraftContentState): RawDraftContentState =>
    draftContent.entityMap ? draftContent : { ...draftContent, entityMap: {} };

/**
 * Update the cell data with safe draft content (i.e. draft content that contains an entityMap object).
 */
const normalizeTableCellDraftContent = (cellData: CellData): CellData => {
    if (!cellData.draftContent) return cellData;

    const normalizedDraftContent = normalizeDraftContentEntityMap(cellData.draftContent);
    if (normalizedDraftContent === cellData.draftContent) return cellData;

    return { ...cellData, draftContent: normalizedDraftContent };
};

/**
 * Restores the Draft.js defaults that can be stripped from persisted V2 table cells,
 * such as an empty `entityMap`, before the content is rehydrated into Draft.js.
 *
 * Mongo will minimise empty objects, so if the entityMap = {}, we need to restore it.
 */
export const normalizeTableContentDataDraftContent = (
    tableContentData: TableContentData | null | undefined,
): TableContentData | undefined => {
    if (!tableContentData) return undefined;

    return tableContentData.map((row) => row.map(normalizeTableCellDraftContent));
};

/**
 * Converts table content data from version 1 to version 2 by parsing Draft.JS content strings
 * into their corresponding Draft.JS objects and extracting the raw text.
 */
export const convertTableContentDataToVersion2 = (
    tableContentData: TableContentData | null | undefined,
): TableContentData => {
    if (!tableContentData) return EMPTY_TABLE_CONTENT_DATA;

    const convertedTableContentData = tableContentData.map((row) =>
        row.map((cellData) => {
            if (!cellData.value || !isCellValueInDraftFormat(cellData.value)) return cellData;

            const draftContent = parseCellContentString(cellData.value);
            if (isString(draftContent) || isNumber(draftContent)) return cellData;

            return { ...cellData, draftContent, value: rawGetText(draftContent) };
        }),
    );

    return normalizeTableContentDataDraftContent(convertedTableContentData) || EMPTY_TABLE_CONTENT_DATA;
};
